import React, { useState, useEffect, useRef, useCallback } from 'react';
import loadable from '@loadable/component';
import { sharedConstants } from 'shared-helpers';
import { Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';

// reusable
import PageHelmet from "../../components/Reusable/PageHelmet";
import VideoHandler from "../../components/Reusable/VideoHandler/VideoHandler";
import { handleRedirectMessage } from '/src/components/Reusable/toast';

// const
import { PUBLIC_IMG_URL } from "/src/lib/endpoints";
import { MAGNITT_ACCESS_ROLES } from '../../lib/constants/userRoles';
import { CONTACT_FORM_TYPE, HUBSPOT_FORM_SOURCE } from '../../lib/constants';
export const TAXONOMIES = sharedConstants.TAXONOMIES;

// components
export const ContactForm = loadable(() => import(/* webpackChunkName: "ContactForm" */ '../../components/ContactForms/ContactForm'));

const AdvancedAnalyticsPage = (props) => {
    const user = useSelector((state) => state.userReducer);

    // check if user is logged in and have access to the page
    const hasAccess = user.is_loggedin && user.roles.includes(MAGNITT_ACCESS_ROLES.advanced_analytics_access);

    if (hasAccess) {
        return <Redirect exact from={`/advanced-analytics`} to={{ pathname: `/analytics/market-sizing` }} />
    }

    const linkRel = [
        {
            rel: "preload",
            href: `${PUBLIC_IMG_URL}/advanced-analytics/aa_charts.webp`,
            as: 'image',
            fetchPriority: "high",
        },
    ]

    // === Features (auto-rotating) ===
    const FEATURES = [
        { key: 'market-sizing', icon: 'fas fa-chart-pie', title: 'Market Sizing', img: 'market-sizing.webp' },
        { key: 'deal-size', icon: 'fas fa-search', title: 'Deal size analysis', img: 'deal-size.webp' },
        { key: 'geo-benchmark', icon: 'fas fa-globe-americas', title: 'Geographical Benchmarking', img: 'geo-benchmark.webp' },
        { key: 'industry-benchmark', icon: 'fas fa-industry', title: 'Industry Benchmarking', img: 'industry-benchmark.webp' },
        { key: 'investor-ranking', icon: 'fas fa-chart-line', title: 'Investor Ranking', img: 'investor-ranking.webp' },
        { key: 'exit-analysis', icon: 'fas fa-sign-out-alt', title: 'Exit Analysis', img: 'exit-analysis.webp' },
        { key: 'startup-analysis', icon: 'fas fa-rocket', title: 'Startup Analysis', img: 'startup-analysis.webp' },
    ];
    const ROTATION_INTERVAL = 3000;
    const [activeIndex, setActiveIndex] = useState(0);
    const featuresSectionRef = useRef(null);
    const [isFeaturesInView, setIsFeaturesInView] = useState(false);
    const userInteractedRef = useRef(false);
    const rotationRef = useRef(null);
    const styleObj = {
        opacity: 1,
        transform: 'translateY(0)',
        transition: 'all .5s ease-in-out',
    }
    const [imgTransitionStyle, setImgTransitionStyle] = useState({ ...styleObj });

    const next = useCallback(() => {
        setActiveIndex(i => (i + 1) % FEATURES.length);
    }, []);

    useEffect(() => {
        if (rotationRef.current) clearInterval(rotationRef.current);
        if (!isFeaturesInView) return;
        rotationRef.current = setInterval(() => {
            if (!userInteractedRef.current) {
                toggleImgStyle();
                next();
            }
        }, ROTATION_INTERVAL);
        return () => rotationRef.current && clearInterval(rotationRef.current);
    }, [next, isFeaturesInView]);

    // Observe features section visibility
    useEffect(() => {
        handleRedirectMessage(props.location);
        const el = featuresSectionRef.current;
        if (!el || typeof window === 'undefined') return;
        const observer = new IntersectionObserver(([entry]) => {
            setIsFeaturesInView(entry.isIntersecting);
        }, { threshold: 0.2 });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const selectFeature = (idx) => {
        toggleImgStyle();
        userInteractedRef.current = true;
        setActiveIndex(idx);
        // re-enable auto after 2 intervals
        setTimeout(() => { userInteractedRef.current = false; }, ROTATION_INTERVAL * 2);
    };

    const toggleImgStyle = () => {
        setImgTransitionStyle({})
        setTimeout(() => {
            setImgTransitionStyle({ ...styleObj })
        }, 500);
    }

    const scrollToAnalyticsForm = () => {
        const scrollOffSet = 120;
        const el = document.getElementById('aa-form');
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.pageYOffset - scrollOffSet;
        window.scrollTo({ top: y, behavior: 'smooth' });
    };

    const activeFeature = FEATURES[activeIndex];

    return (
        <div className="footer-margin-offset">

            <PageHelmet
                pageURL="advanced-analytics"
                pageTitle="Advanced Analytics"
                pageDescription="MAGNiTT Advanced Analytics"
                previewImageUrl={`${PUBLIC_IMG_URL}/advanced-analytics/aa_barchart.png`}
                linkRel={linkRel}
            />

            <section className="aa-hero">
                <div className="aa-hero__inner">
                    <div className="aa-hero__copy">
                        <h1 className="aa-hero__title">Real-Time Trends &amp; <br /> Comparisons</h1>
                        <p className="aa-hero__subtitle">by Industry, Geography &amp; Funding Stage</p>
                    </div>
                    <div className="aa-hero__art">
                        <img
                            src={`${PUBLIC_IMG_URL}/advanced-analytics/aa_charts.webp`}
                            alt="Real-time trends visualization"
                            className="aa-hero__img"
                            loading="eager"
                            width="640"
                            height="360.28"
                        />
                    </div>
                </div>
                <div className="aa-hero__bg"></div>
            </section>

            <section className="aa_form" id="aa-form">
                <div className="form-container">
                    <div className="mx-auto mb-0">
                        <div className="db-box no-background h-100 text-section-container">
                            <div className="text-section">
                                <h2 className="aa-form-title">Want to Predict<br /> the Next Big VC Move?</h2>
                                <p>Our Advanced Analytics tool provides you with real-time charts on funding, deal and exits evolution where you can analyze information by industry, geography, funding stage and round brackets.</p>
                                <p>Understand percentage shares, mean and medians, and ranking by industry, geography, funding stage and round brackets. This tool provides side by side trend comparisons on geographies, industries and investors.</p>
                                <p>Reach out, dive into the data, and get smart on MEAPT trends!</p>
                            </div>
                        </div>
                    </div>

                    <ContactForm
                        taxonomyType={TAXONOMIES.salesContactOptions}
                        formType={CONTACT_FORM_TYPE.enterprise}
                        buttonLabel='Contact Us'
                        page={HUBSPOT_FORM_SOURCE.analytics}
                    />
                </div>
            </section>

            <section className='aa-video'>
                <div className='aa-video-container'>
                    <div className='aa-video-title'>
                        <h2>See Advanced Analytics in Action</h2>
                    </div>

                    <div className='aa-video-frame'>
                        <VideoHandler
                            src={`${PUBLIC_IMG_URL}/advanced-analytics/see-advanced-analytics-in-action.mp4`}
                            title="See Advanced Analytics in Action"
                            className="aa-video-lite"
                            preloadOnView={true}
                            rootMargin="200px"
                            poster={`${PUBLIC_IMG_URL}/advanced-analytics/see-advanced-analytics-in-action.webp`}
                        />
                    </div>
                </div>
            </section>

            <section className='aa-features' ref={featuresSectionRef}>
                <div className='aa-features-container'>
                    <div className='aa-features-title'>
                        <h2>Advanced Analytics Includes</h2>
                    </div>
                    <div className='aa-features-grid'>
                        <div className='aa-features-visual'>
                            <div className='aa-features-image-frame'>
                                <img
                                    key={activeFeature.key}
                                    src={`${PUBLIC_IMG_URL}/advanced-analytics/${activeFeature.img}`}
                                    alt={activeFeature.title}
                                    className='aa-features-img'
                                    style={imgTransitionStyle}
                                    loading='lazy'
                                />
                            </div>
                        </div>
                        <ul className='aa-features-list' role='tablist' aria-label='Advanced analytics features'>
                            {FEATURES.map((f, idx) => {
                                const isActive = idx === activeIndex;
                                return (
                                    <li
                                        key={f.key}
                                        role='tab'
                                        aria-selected={isActive}
                                        tabIndex={0}
                                        className={`aa-feature-item${isActive ? ' is-active' : ''}`}
                                        onClick={() => selectFeature(idx)}
                                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && selectFeature(idx)}
                                    >
                                        <span className='aa-feature-icon'><i className={f.icon} /></span>
                                        <span className='aa-feature-label'>{f.title}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    <div className='aa-features-cta'>
                        <button onClick={scrollToAnalyticsForm} className='btn btn-main btn-block aa-contact-btn'>Contact Us <i className="fas fa-chevron-right"></i></button>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default AdvancedAnalyticsPage;
